import { useLivePlayer } from '@/hooks/useLivePlayer'
import { FALLBACK_HOST, STATION_LOGO, imageSrc } from '@/lib/images'
import { formatSlot } from '@/lib/time'
import type { RadioSettings, TeamMember } from '@/types/radio'
import { Loader2, Minus, Pause, Play, Plus, Radio, Volume1, Volume2, VolumeX } from 'lucide-react'

interface LiveBarProps {
  settings: RadioSettings | null
  current?: TeamMember | null
}

function LiveBar({ settings, current }: LiveBarProps) {
  const { playing, loading, volume, setVolume, muted, toggleMute, volumeSupported, toggle, error } = useLivePlayer(
    settings?.streamUrl,
  )
  const live = Boolean(current)
  const photo = imageSrc(current?.photoUrl || settings?.logoUrl, live ? FALLBACK_HOST : STATION_LOGO)
  const title = current?.programName || 'Programação musical'
  const host = current?.name || settings?.stationName || 'Bonita FM'
  const slot = current ? formatSlot(current.startTime, current.endTime) : '24 horas no ar'

  const statusLabel = loading ? 'CONECTANDO...' : playing ? 'PAUSAR' : 'OUVIR AO VIVO'
  const buttonLabel = loading ? 'Conectando à rádio' : playing ? 'Pausar' : 'Reproduzir'
  const VolumeIcon = muted || volume === 0 ? VolumeX : volume < 0.5 ? Volume1 : Volume2
  const shownVolume = muted ? 0 : volume
  const stepVolume = (delta: number) => setVolume(Math.round((shownVolume + delta) * 10) / 10)

  return (
    <section className="mx-auto max-w-7xl px-3 sm:px-4 lg:px-6">
      <div
        className={`flex flex-col gap-3 rounded-[24px] border px-3 py-3 transition-colors duration-500 sm:rounded-[28px] sm:px-4 md:flex-row md:items-center ${
          playing
            ? 'player-live border-[#18c8f5]/70'
            : 'border-white/10 bg-[#0a2aa0]/70 shadow-[0_10px_40px_rgba(0,0,0,0.25)]'
        }`}
      >
        <div className="flex min-w-0 flex-1 items-center gap-3">
          <div className={`relative shrink-0 rounded-full ${playing ? 'player-photo-live' : ''}`}>
            <img
              src={photo}
              alt={host}
              className={`h-12 w-12 rounded-full object-cover ring-2 sm:h-14 sm:w-14 ${
                playing ? 'ring-[#18c8f5]' : 'ring-[#ffd21f]'
              }`}
            />
          </div>
          <button
            type="button"
            onClick={() => void toggle()}
            className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-[#ffd21f] text-[#0b2ea8] transition active:scale-95"
            aria-label={buttonLabel}
            aria-busy={loading}
          >
            {loading ? (
              <Loader2 size={20} className="animate-spin" />
            ) : playing ? (
              <Pause size={18} fill="currentColor" />
            ) : (
              <Play size={18} fill="currentColor" />
            )}
          </button>
          <div className="min-w-0">
            <p className="flex items-center gap-2 text-[11px] font-extrabold tracking-[0.16em] text-[#ffd21f]">
              AGORA NO AR
              {playing ? (
                <span className="inline-flex items-center gap-1 rounded-full bg-red-600 px-2 py-0.5 text-[9px] tracking-wider text-white">
                  <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-white" />
                  TOCANDO
                </span>
              ) : null}
            </p>
            <p className="truncate font-display text-lg font-black leading-tight">{title}</p>
            <p className="truncate text-sm text-white/75">{live ? `Com ${host} · ${slot}` : host}</p>
          </div>
        </div>

        <div className="flex h-8 flex-1 items-end justify-center gap-[3px] pb-1 md:h-10" aria-hidden="true">
          {Array.from({ length: 28 }).map((_, index) => (
            <span
              key={index}
              className={`eq-bar w-[4px] rounded-full ${playing ? 'bg-gradient-to-t from-[#18c8f5] to-[#ffd21f]' : 'bg-[#18c8f5]'}`}
              style={{
                height: `${10 + ((index * 17) % 24)}px`,
                animationDelay: `${index * 0.05}s`,
                animationPlayState: playing ? 'running' : 'paused',
                opacity: playing ? 1 : 0.35,
              }}
            />
          ))}
        </div>

        <div className="flex items-center justify-between gap-3 sm:justify-end">
          <button
            type="button"
            onClick={() => void toggle()}
            disabled={false}
            aria-busy={loading}
            className={`inline-flex min-w-[10.5rem] flex-1 items-center justify-center gap-2 rounded-full px-4 py-3 text-[12px] font-black tracking-wide transition active:scale-95 sm:flex-none sm:px-5 ${
              playing ? 'bg-white text-[#0b2ea8]' : 'bg-[#ffd21f] text-[#0b2ea8]'
            }`}
          >
            {loading ? <Loader2 size={16} className="animate-spin" /> : <Radio size={16} />}
            {statusLabel}
          </button>

          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={toggleMute}
              aria-label={muted ? 'Ativar som' : 'Silenciar'}
              aria-pressed={muted}
              className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-white/10 text-white transition hover:bg-white/20 active:scale-95"
            >
              <VolumeIcon size={20} />
            </button>
            {volumeSupported ? (
              <input
                type="range"
                min={0}
                max={1}
                step={0.01}
                value={shownVolume}
                onChange={(event) => setVolume(Number(event.target.value))}
                aria-label="Volume"
                className="hidden h-8 w-24 touch-pan-x accent-[#ffd21f] sm:block"
              />
            ) : null}
          </div>
        </div>
      </div>

      {volumeSupported ? (
        <div className="mt-2 flex items-center gap-2 rounded-full bg-white/10 px-2 py-1 sm:hidden">
          <button
            type="button"
            onClick={() => stepVolume(-0.1)}
            aria-label="Diminuir volume"
            className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-white/10 text-white active:scale-90 active:bg-white/25"
          >
            <Minus size={20} />
          </button>
          <input
            type="range"
            min={0}
            max={1}
            step={0.01}
            value={shownVolume}
            onChange={(event) => setVolume(Number(event.target.value))}
            aria-label="Volume"
            className="h-9 min-w-0 flex-1 touch-pan-x accent-[#ffd21f]"
          />
          <button
            type="button"
            onClick={() => stepVolume(0.1)}
            aria-label="Aumentar volume"
            className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-[#ffd21f] text-[#0b2ea8] active:scale-90"
          >
            <Plus size={20} />
          </button>
        </div>
      ) : (
        <p className="mt-2 rounded-full bg-white/10 px-4 py-2 text-center text-xs text-white/80 sm:hidden">
          Neste aparelho, ajuste o volume pelos botões laterais do telemóvel.
        </p>
      )}

      <p role="status" aria-live="polite" className="mt-2 min-h-[1rem] text-center text-xs text-amber-200">
        {error || (loading ? 'Conectando à rádio ao vivo…' : '')}
      </p>
    </section>
  )
}

export default LiveBar
